import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const AmbassadorProgram = () => {
  const tiers = [
    {
      name: 'Explorer',
      requirement: '1-5 referrals',
      color: 'from-blue-500 to-blue-600',
      benefits: [
        '15% recurring commission on all referrals',
        'Personal referral link and tracking dashboard',
        'Access to marketing assets and templates',
        'Monthly performance reports',
        'Email support from the partner team'
      ]
    },
    {
      name: 'Advocate',
      requirement: '6-20 referrals',
      color: 'from-purple-500 to-purple-600',
      benefits: [
        '20% recurring commission on all referrals',
        'Everything in Explorer tier',
        'Co-branded landing pages',
        'Priority partner support',
        'Early access to new features',
        'Quarterly strategy calls'
      ]
    },
    {
      name: 'Champion',
      requirement: '21+ referrals',
      color: 'from-orange-500 to-orange-600',
      benefits: [
        '30% recurring commission on all referrals',
        'Everything in Advocate tier',
        'Custom discount codes for your audience',
        'Featured on our Partners page',
        'Dedicated partner manager',
        'Annual SERPfinity summit invite',
        'Free Enterprise plan for personal use'
      ]
    }
  ];

  const howItWorks = [
    { step: '1', title: 'Apply & Get Approved', description: 'Submit your application with details about your audience and how you plan to promote SERPfinity. We review applications within 48 hours.', icon: '' },
    { step: '2', title: 'Get Your Unique Link', description: 'Once approved, you will receive a personalized referral link, tracking dashboard, and marketing materials to start promoting.', icon: '' },
    { step: '3', title: 'Share & Promote', description: 'Share SERPfinity with your audience through blog posts, social media, email newsletters, webinars, or any channel that works for you.', icon: '' },
    { step: '4', title: 'Earn Commissions', description: 'Earn recurring commissions every month for as long as your referrals remain active subscribers. Payouts are made monthly via PayPal or bank transfer.', icon: '' }
  ];

  const idealAmbassadors = [
    { title: 'SEO Professionals & Agencies', description: 'Digital marketing experts who serve clients needing AI search visibility tools.', icon: '' },
    { title: 'Content Creators & Bloggers', description: 'Influencers in the SEO, marketing, or tech space with an engaged audience.', icon: '' },
    { title: 'Marketing Consultants', description: 'Independent consultants who recommend tools to their clients as part of their strategy.', icon: '' },
    { title: 'Course Creators & Educators', description: 'Online educators teaching SEO, digital marketing, or content creation.', icon: '' },
    { title: 'YouTube & Podcast Hosts', description: 'Creators producing content about SEO, AI, marketing, and online business.', icon: '' },
    { title: 'Community Leaders', description: 'Moderators of SEO, marketing, or tech communities with influence among peers.', icon: '' }
  ];

  const faqs = [
    { q: 'When will the Ambassador Program launch?', a: 'The SERPfinity Ambassador Program is currently in development and coming soon. Join the waitlist to be among the first to participate when we launch.' },
    { q: 'How much can I earn?', a: 'Earnings depend on your referral volume and tier level. Commissions range from 15% to 30% recurring monthly for the lifetime of each referred subscription. Top ambassadors can earn thousands per month.' },
    { q: 'How are commissions tracked?', a: 'Each ambassador receives a unique referral link with a 90-day cookie window. All referrals are tracked automatically through your partner dashboard, giving you real-time visibility into clicks, signups, and conversions.' },
    { q: 'When and how are commissions paid?', a: 'Commissions are paid monthly, 30 days after the referred subscription payment clears. Payment options include PayPal, direct bank transfer, and Wise (for international partners). Minimum payout threshold is $50.' },
    { q: 'Can I promote SERPfinity on any platform?', a: 'Yes, you can promote on any legitimate platform including your website, blog, YouTube, social media, email newsletters, and online communities. We provide marketing guidelines to ensure brand consistency.' },
    { q: 'Do I need technical knowledge?', a: 'No technical expertise is required. We provide all the marketing materials, training resources, and support you need. A basic understanding of SEO and digital marketing is helpful but not mandatory.' },
    { q: 'Is there a cost to join?', a: 'The Ambassador Program is completely free to join. There are no fees, minimum commitments, or hidden costs. You only need to meet the approval criteria.' }
  ];

  return (
    <>
      <Helmet>
        <title>Ambassador Program | Earn Commissions with SERPfinity</title>
        <meta name="description" content="Join the SERPfinity Ambassador Program and earn commissions promoting AI-powered SEO and AEO tools. Get marketing resources, tracking links, and dedicated support." />
            <meta property="og:title" content="Ambassador Program | Earn Commissions with SERPfinity" />
            <meta property="og:description" content="Join the SERPfinity Ambassador Program and earn commissions promoting AI-powered SEO and AEO tools. Get marketing resources, tracking links, and dedicated support." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://app.serpfinity.ai/ambassador" />
            <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
            <meta property="og:site_name" content="SERPfinity" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Ambassador Program | Earn Commissions with SERPfinity" />
            <meta name="twitter:description" content="Join the SERPfinity Ambassador Program and earn commissions promoting AI-powered SEO and AEO tools." />
            <meta name="twitter:image" content="https://app.serpfinity.ai/og-image.png" />

      </Helmet>
    <div className="min-h-screen bg-white">
      {/* Hero with Coming Soon Banner */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 bg-gradient-to-l from-orange-500 to-yellow-500 text-white px-8 py-2 text-sm font-bold transform rotate-0 shadow-lg rounded-bl-lg">
          COMING SOON
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-1 bg-orange-500 bg-opacity-20 border border-orange-400 rounded-full text-orange-300 text-sm font-semibold mb-6">
            Program Launching Soon
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Ambassador Program</h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto mb-8">
            Join the SERPfinity Ambassador Program and earn recurring commissions by sharing the future of AI-powered search visibility with your audience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:info@serpfinity.ai?subject=Ambassador Program Waitlist&body=I would like to join the SERPfinity Ambassador Program waitlist.%0A%0AName:%0AWebsite/Social:%0AAudience size:%0AHow I plan to promote:" className="inline-block px-8 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-yellow-600 transition shadow-lg">
              Join the Waitlist
            </a>
            <a href="#program-details" className="inline-block px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:bg-opacity-10 transition">
              Learn More
            </a>
          </div>
        </div>
      </div>

      {/* Key Stats */}
      <div className="bg-slate-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-blue-600">Up to 30%</p>
              <p className="text-slate-600 mt-1">Recurring Commission</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-purple-600">90-Day</p>
              <p className="text-slate-600 mt-1">Cookie Window</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">Monthly</p>
              <p className="text-slate-600 mt-1">Payouts</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-600">Lifetime</p>
              <p className="text-slate-600 mt-1">Commission Duration</p>
            </div>
          </div>
        </div>
      </div>

      <div id="program-details" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* How It Works */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">How It Works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl mb-4">{step.icon}</div>
                <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">{step.step}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Commission Tiers */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">Commission Tiers</h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            The more referrals you bring, the higher your commission rate. All tiers earn recurring commissions for the lifetime of each referral.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((tier, idx) => (
              <div key={idx} className="rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow">
                <div className={"bg-gradient-to-r " + tier.color + " text-white p-6 text-center"}>
                  <h3 className="text-2xl font-bold">{tier.name}</h3>
                  <p className="text-white text-opacity-80 mt-1">{tier.requirement}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {tier.benefits.map((benefit, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-slate-600 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ideal Ambassadors */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">Who Should Apply?</h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Our program is designed for anyone passionate about SEO and digital marketing who can authentically recommend SERPfinity to their audience.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {idealAmbassadors.map((type, idx) => (
              <div key={idx} className="p-6 bg-slate-50 rounded-xl border border-slate-200 hover:border-blue-300 transition">
                <span className="text-3xl block mb-3">{type.icon}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{type.title}</h3>
                <p className="text-slate-600 text-sm">{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Marketing Resources */}
        <div className="mb-20 p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Marketing Resources We Provide</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Banner Ads', desc: 'High-converting display ads in multiple sizes for your website or blog.', icon: '' },
              { title: 'Email Templates', desc: 'Pre-written email sequences to introduce SERPfinity to your subscribers.', icon: '' },
              { title: 'Social Media Kit', desc: 'Ready-to-post graphics and captions for all major social platforms.', icon: '' },
              { title: 'Video Scripts', desc: 'Talking points and demo scripts for YouTube, webinars, and tutorials.', icon: '' }
            ].map((resource, idx) => (
              <div key={idx} className="text-center">
                <span className="text-3xl block mb-2">{resource.icon}</span>
                <h4 className="font-semibold text-slate-900 mb-1">{resource.title}</h4>
                <p className="text-slate-600 text-xs">{resource.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">Program FAQ</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group border border-slate-200 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-slate-800 hover:bg-slate-50 transition">
                  {faq.q}
                  <svg className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-4 text-slate-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-16 px-8 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">Be Among the First Ambassadors</h2>
          <p className="text-white text-opacity-90 text-lg mb-8 max-w-2xl mx-auto">
            Join our waitlist today and get priority access when the Ambassador Program launches. Early members will receive exclusive benefits and bonus commission rates.
          </p>
          <a href="mailto:info@serpfinity.ai?subject=Ambassador Program Waitlist&body=I would like to join the SERPfinity Ambassador Program waitlist.%0A%0AName:%0AWebsite/Social:%0AAudience size:%0AHow I plan to promote:" className="inline-block px-10 py-4 bg-white text-orange-600 font-bold rounded-lg hover:bg-orange-50 transition shadow-lg text-lg">
            Join the Waitlist
          </a>
        </div>

      </div>
    </div>
      </>
  );
};

export default AmbassadorProgram;
